import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Alert,
  Button,
  Empty,
  Input,
  message,
  Modal,
  ModalProps,
  Popconfirm,
  Popover,
  Select,
  Space,
  Spin,
  Switch,
  Tooltip,
  Typography,
} from "antd";
import { ApiService } from "api/api.service";
import { memo, useEffect, useMemo, useState } from "react";
import {
  TbChevronDown,
  TbFolderPlus,
  TbGripVertical,
  TbPlus,
  TbSearch,
  TbTemplate,
  TbTrash,
  TbX,
} from "react-icons/tb";
import { COLUMN_ICONS, GROUP_COLOR_PALETTE } from "./MetaItems";
import { BoardResource } from "dtos/kanban.dto";
import { ActionPicker } from "./ActionPicker";

const { Text } = Typography;

export type ColumnDto = {
  id: string;
  name: string;
  group?: string;
  position: number;
  meta?: Record<string, any>;
  disabled: boolean;
};

interface EditColumnsProps extends ModalProps {
  boardId: string | undefined;
  refetch?: () => unknown;
}

type DraftColumn = ColumnDto & { _key: string; action?: string };

type DraftGroup = {
  id: string;
  name: string;
  meta?: { color?: string; [k: string]: any };
};
const UNGROUPED = "__ungrouped__";

const newId = () => `col_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;

const getPaletteEntry = (color: string | undefined, fallbackIdx: number) => {
  const found = GROUP_COLOR_PALETTE.find((p) => p.solid === color);
  return found ?? GROUP_COLOR_PALETTE[fallbackIdx % GROUP_COLOR_PALETTE.length];
};

const pickGroupColor = (existing: DraftGroup[]): string => {
  const used = new Set(existing.map((g) => g.meta?.color).filter(Boolean) as string[]);
  const available = GROUP_COLOR_PALETTE.filter((c) => !used.has(c.solid));
  const pool = available.length ? available : GROUP_COLOR_PALETTE;
  return pool[Math.floor(Math.random() * pool.length)].solid;
};

const pickRandomIcon = () => {
  const entries = Object.keys(COLUMN_ICONS);
  return entries[Math.floor(Math.random() * entries.length)];
};

const renderColumnIcon = (name: string | undefined, size = 16, color?: string) => {
  const Icon = (name && COLUMN_ICONS[name]) || COLUMN_ICONS.TbCircleDashed;
  return <Icon size={size} color={color} />;
};

interface ColorPickerProps {
  value?: string;
  onChange?: (val: string) => void;
}

const ColorPicker = memo(function ColorPicker({ value, onChange }: ColorPickerProps) {
  const [open, setOpen] = useState(false);
  const palette = getPaletteEntry(value, 0);

  const content = (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 6, width: 224 }}>
      {GROUP_COLOR_PALETTE.map((c) => {
        const selected = c.solid === value;
        return (
          <button
            key={c.solid}
            type="button"
            onClick={() => {
              onChange?.(c.solid);
              setOpen(false);
            }}
            style={{
              width: 22,
              height: 22,
              borderRadius: "50%",
              background: c.solid,
              border: "none",
              cursor: "pointer",
              padding: 0,
              boxShadow: selected ? `0 0 0 2px #fff, 0 0 0 4px ${c.solid}` : "none",
            }}
          />
        );
      })}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: "50%",
          background: palette.solid,
          boxShadow: `0 0 0 3px ${palette.soft}`,
          border: "none",
          padding: 0,
          cursor: "pointer",
          flexShrink: 0,
        }}
      />
    </Popover>
  );
});

interface IconPickerProps {
  value?: string;
  onChange?: (val: string) => void;
  accentColor?: string;
}

const IconPicker = memo(function IconPicker({ value, onChange, accentColor }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const entries = useMemo(() => {
    const all = Object.entries(COLUMN_ICONS);
    if (!search.trim()) return all;
    const q = search.toLowerCase().replace(/^tb/, "");
    return all.filter(([key]) => key.toLowerCase().replace(/^tb/, "").includes(q));
  }, [search]);

  const content = (
    <div style={{ width: 240 }}>
      <Input
        size="small"
        placeholder="Search icons"
        prefix={<TbSearch size={12} />}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ marginBottom: 8 }}
        autoFocus
      />
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4, maxHeight: 220, overflowY: "auto" }}
      >
        {entries.map(([key, Icon]) => {
          const selected = key === value;
          return (
            <button
              key={key}
              type="button"
              title={key.replace(/^Tb/, "")}
              onClick={() => {
                onChange?.(key);
                setOpen(false);
              }}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 28,
                height: 28,
                border: selected ? `1px solid ${accentColor ?? "#6d28d9"}` : "1px solid transparent",
                borderRadius: 6,
                background: selected ? `${accentColor ?? "#6d28d9"}14` : "transparent",
                color: selected ? (accentColor ?? "#6d28d9") : "#374151",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <Icon size={15} />
            </button>
          );
        })}
        {entries.length === 0 && (
          <div style={{ gridColumn: "1 / -1", textAlign: "center", color: "#9ca3af", fontSize: 12, padding: 8 }}>
            No icons found
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={content}
      destroyTooltipOnHide
    >
      <button
        type="button"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 36,
          height: 36,
          borderRadius: 8,
          border: `1px solid ${accentColor ? `${accentColor}33` : "#e5e7eb"}`,
          background: accentColor ? `${accentColor}10` : "#fff",
          color: accentColor ?? "#6b7280",
          cursor: "pointer",
          flexShrink: 0,
        }}
      >
        {renderColumnIcon(value, 18, accentColor)}
      </button>
    </Popover>
  );
});

interface SortableColumnProps {
  column: DraftColumn;
  accentColor?: string;
  resource: BoardResource;
  orgCode?: string;
  onChange: (patch: Partial<DraftColumn>) => void;
  onMetaChange: (metaPatch: Record<string, any>) => void;
  onDelete: () => void;
}

const SortableColumn = ({
  column,
  accentColor,
  resource,
  orgCode,
  onChange,
  onMetaChange,
  onDelete,
}: SortableColumnProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column._key,
    data: { type: "column", groupId: column.group ?? UNGROUPED },
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const description = (column.meta?.description as string | undefined) ?? "";
  const iconName = column.meta?.icon as string | undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded-md bg-white px-2 py-2"
      onMouseEnter={undefined}
    >
      <span
        {...attributes}
        {...listeners}
        className="flex cursor-grab items-center text-gray-300 hover:text-gray-500 active:cursor-grabbing pt-2"
      >
        <TbGripVertical size={16} />
      </span>

      <IconPicker value={iconName} onChange={(name) => onMetaChange({ icon: name })} accentColor={accentColor} />

      <div className="flex flex-col flex-1 min-w-0 border border-solid border-gray-100 rounded-md px-2 py-1 hover:border-gray-200">
        <Input
          value={column.name}
          size="small"
          placeholder="Column name"
          variant="borderless"
          onChange={(e) => onChange({ name: e.target.value })}
          status={!column.name.trim() ? "error" : undefined}
          style={{ padding: "2px 0", fontWeight: 500, fontSize: 13 }}
        />
        <Input
          value={description}
          size="small"
          placeholder="Description (optional)"
          variant="borderless"
          onChange={(e) => onMetaChange({ description: e.target.value })}
          style={{ padding: "2px 0", fontSize: 12, color: "#6b7280" }}
        />
      </div>
      <div className="pt-1.5">
        <ActionPicker
          resource={resource}
          orgCode={orgCode}
          value={column.action}
          onChange={(action) => onChange({ action })}
        />
      </div>

      <Tooltip title={column.disabled ? "Disabled" : "Enabled"}>
        <div className="pt-2">
          <Switch size="small" checked={!column.disabled} onChange={(checked) => onChange({ disabled: !checked })} />
        </div>
      </Tooltip>

      {/* <Popover
        open={open}
        onOpenChange={setOpen}
        trigger="click"
        placement="bottomLeft"
        title="Choose Template"
        content={
          <Select
            showSearch
            style={{ width: 220 }}
            placeholder="Choose a template..."
            options={TEMPLATE_OPTIONS}
            onChange={(value) => {
              // onSelect(value);
              setOpen(false);
            }}
          />
        }
      >
        <Tooltip title="Click to select template">
          <Button size="small" className="!mt-1" icon={<TbTemplate size={14} />} />
        </Tooltip>
      </Popover> */}

      <Popconfirm
        title="Delete this column?"
        description="Cards in this column will need to be reassigned."
        okText="Delete"
        okButtonProps={{ danger: true }}
        onConfirm={onDelete}
      >
        <Button type="text" size="small" danger icon={<TbX size={14} />} className="!mt-1" />
      </Popconfirm>
    </div>
  );
};

interface SortableGroupProps {
  groupId: string;
  groupName: string;
  isUngrouped: boolean;
  color?: string;
  paletteIdx: number;
  columns: DraftColumn[];
  resource: BoardResource;
  orgCode?: string;
  collapsed: boolean;
  onToggleCollapsed: () => void;
  onRenameGroup: (newName: string) => void;
  onColorChange: (color: string) => void;
  onDeleteGroup: () => void;
  onAddColumn: () => void;
  onColumnChange: (key: string, patch: Partial<DraftColumn>) => void;
  onColumnMetaChange: (key: string, metaPatch: Record<string, any>) => void;
  onColumnDelete: (key: string) => void;
}

const SortableGroup = ({
  groupId,
  groupName,
  isUngrouped,
  color,
  paletteIdx,
  columns,
  resource,
  orgCode,
  collapsed,
  onToggleCollapsed,
  onRenameGroup,
  onColorChange,
  onDeleteGroup,
  onAddColumn,
  onColumnChange,
  onColumnMetaChange,
  onColumnDelete,
}: SortableGroupProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: groupId,
    data: { type: "group" },
    disabled: isUngrouped,
  });

  const palette = getPaletteEntry(color, paletteIdx);

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    border: isUngrouped ? "1px solid #f0f0f0" : `1px solid ${palette.border}`,
    borderLeft: isUngrouped ? "1px solid #f0f0f0" : `3px solid ${palette.solid}`,
    borderRadius: 8,
    marginBottom: 6,
    overflow: "hidden",
    background: "#fff",
    boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="flex items-center gap-2 px-3 py-2" style={{ background: isUngrouped ? "#fafafa" : palette.soft }}>
        {!isUngrouped && (
          <span
            {...attributes}
            {...listeners}
            className="flex cursor-grab items-center text-gray-400 hover:text-gray-600 active:cursor-grabbing"
          >
            <TbGripVertical size={16} />
          </span>
        )}

        {isUngrouped ? (
          <span style={{ width: 14, height: 14, display: "inline-block" }} />
        ) : (
          <ColorPicker value={color} onChange={onColorChange} />
        )}

        {isUngrouped ? (
          <Text style={{ fontSize: 12, color: "#6b7280", fontWeight: 500, flex: 1 }}>Ungrouped columns</Text>
        ) : (
          <Input
            size="small"
            value={groupName}
            placeholder="Group name"
            variant="borderless"
            onChange={(e) => onRenameGroup(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{
              fontWeight: 600,
              background: "transparent",
              padding: 0,
              color: "#111827",
              flex: 1,
            }}
          />
        )}

        <Text type="secondary" style={{ fontSize: 12 }}>
          {columns.length}
        </Text>

        <button
          type="button"
          onClick={onToggleCollapsed}
          style={{
            border: "none",
            background: "transparent",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            padding: 4,
          }}
        >
          <TbChevronDown
            size={16}
            style={{
              transform: collapsed ? "none" : "rotate(180deg)",
              transition: "transform 0.2s",
              color: "#6b7280",
            }}
          />
        </button>

        {!isUngrouped && (
          <Popconfirm
            title="Delete this group?"
            description="Columns inside will move to Ungrouped."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={onDeleteGroup}
          >
            <Tooltip title="Delete group">
              <Button
                type="text"
                size="small"
                danger
                icon={<TbTrash size={14} />}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        )}
      </div>

      {!collapsed && (
        <div style={{ padding: "8px 10px 12px" }}>
          <SortableContext items={columns.map((c) => c._key)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-1 min-h-[40px]">
              {columns.length === 0 && (
                <div className="rounded-md border border-dashed border-gray-300 px-3 py-2 text-center text-xs text-gray-400">
                  Drop columns here
                </div>
              )}
              {columns.map((col) => (
                <SortableColumn
                  key={col._key}
                  column={col}
                  accentColor={isUngrouped ? "#9ca3af" : palette.solid}
                  resource={resource}
                  orgCode={orgCode}
                  onChange={(patch) => onColumnChange(col._key, patch)}
                  onMetaChange={(metaPatch) => onColumnMetaChange(col._key, metaPatch)}
                  onDelete={() => onColumnDelete(col._key)}
                />
              ))}
            </div>
          </SortableContext>

          <Button
            type="text"
            size="small"
            icon={<TbPlus size={13} />}
            onClick={onAddColumn}
            style={{
              color: isUngrouped ? "#9ca3af" : palette.solid,
              width: "100%",
              marginTop: 6,
              fontWeight: 500,
              justifyContent: "center",
            }}
          >
            Add Column
          </Button>
        </div>
      )}
    </div>
  );
};

export const EditColumnsModal = ({ boardId, ...props }: EditColumnsProps) => {
  const title = props.title ?? "Edit Columns";
  const okText = props.okText ?? "Update";

  const getBoardQuery = ApiService.kanban.useGetBoardData(boardId);
  const editColumns = ApiService.kanban.useEditColumns();

  const serverData = getBoardQuery.data?.result;
  const serverColumns = serverData?.columns;
  const resource = serverData?.resource;
  const serverGroups = serverData?.groups as
    | { name: string; position: number; meta?: { color?: string } }[]
    | undefined;
  const orgCode = useMemo(() => {
    const org = serverData?.organisation;
    return typeof org === "object" && org !== null ? (org as { code: string }).code : undefined;
  }, [serverData?.organisation]);

  const [columns, setColumns] = useState<DraftColumn[]>([]);
  const [groups, setGroups] = useState<DraftGroup[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (!props.open) return;
    if (!serverColumns) return;

    const sortedCols = [...serverColumns].sort((a, b) => a.position - b.position);

    if (serverGroups?.length) {
      const sortedGroups = [...serverGroups].sort((a, b) => a.position - b.position);

      const mappedGroups: DraftGroup[] = sortedGroups.map((g) => ({
        id: crypto.randomUUID(),
        name: g.name,
        meta: g.meta ?? {},
      }));

      setGroups(mappedGroups);

      // map old group names -> ids
      const groupMap = new Map(mappedGroups.map((g) => [g.name, g.id]));

      setColumns(
        sortedCols.map((c) => ({
          ...c,
          _key: c.id,
          group: c.group ? groupMap.get(c.group) : undefined,
        })),
      );
    } else {
      const seen: string[] = [];

      for (const c of sortedCols) {
        const g = c.group;
        if (g && !seen.includes(g)) seen.push(g);
      }

      const mappedGroups: DraftGroup[] = seen.map((name, idx) => ({
        id: crypto.randomUUID(),
        name,
        meta: {
          color: GROUP_COLOR_PALETTE[idx % GROUP_COLOR_PALETTE.length].solid,
        },
      }));

      setGroups(mappedGroups);

      const groupMap = new Map(mappedGroups.map((g) => [g.name, g.id]));

      setColumns(
        sortedCols.map((c) => ({
          ...c,
          _key: c.id,
          group: c.group ? groupMap.get(c.group) : undefined,
        })),
      );
    }

    setCollapsedGroups({});
  }, [props.open, serverColumns, serverGroups]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const groupOrder = useMemo(() => groups.map((g) => g.id), [groups]);

  const grouped = useMemo(() => {
    const map = new Map<string, DraftColumn[]>();

    for (const g of groupOrder) {
      map.set(g, []);
    }

    map.set(UNGROUPED, []);

    for (const c of columns) {
      const key = c.group ?? UNGROUPED;

      if (!map.has(key)) {
        map.set(key, []);
      }

      map.get(key)!.push(c);
    }

    return map;
  }, [columns, groupOrder]);

  const renderGroupIds = useMemo(() => [...groupOrder, UNGROUPED], [groupOrder]);

  const updateColumn = (key: string, patch: Partial<DraftColumn>) =>
    setColumns((prev) => prev.map((c) => (c._key === key ? { ...c, ...patch } : c)));

  const updateColumnMeta = (key: string, metaPatch: Record<string, any>) =>
    setColumns((prev) => prev.map((c) => (c._key === key ? { ...c, meta: { ...(c.meta ?? {}), ...metaPatch } } : c)));

  const deleteColumn = (key: string) => setColumns((prev) => prev.filter((c) => c._key !== key));

  const addColumn = (group?: string) => {
    const id = newId();
    setColumns((prev) => [
      ...prev,
      {
        _key: id,
        id,
        name: "New Column",
        group,
        position: prev.length,
        disabled: false,
        meta: { icon: pickRandomIcon() },
      },
    ]);
  };

  const addGroup = () => {
    let i = 1;
    let name = `New Group`;

    const existing = new Set(groups.map((g) => g.name));

    while (existing.has(name)) {
      i += 1;
      name = `New Group ${i}`;
    }

    setGroups((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        name,
        meta: {
          color: pickGroupColor(prev),
        },
      },
    ]);
  };

  const renameGroup = (id: string, newName: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === id
          ? {
              ...g,
              name: newName,
            }
          : g,
      ),
    );
  };
  const setGroupColor = (groupId: string, color: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === groupId
          ? {
              ...g,
              meta: {
                ...(g.meta ?? {}),
                color,
              },
            }
          : g,
      ),
    );
  };

  const deleteGroup = (id: string) => {
    setGroups((prev) => prev.filter((g) => g.id !== id));

    setColumns((prev) =>
      prev.map((c) =>
        c.group === id
          ? {
              ...c,
              group: undefined,
            }
          : c,
      ),
    );
  };

  const toggleCollapsed = (id: string) => setCollapsedGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  const findContainerOf = (key: string): string | null => {
    if (renderGroupIds.includes(key)) return key;
    for (const [gid, cols] of grouped.entries()) {
      if (cols.some((c) => c._key === key)) return gid;
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => setActiveId(String(event.active.id));

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    if (activeIdStr === overIdStr) return;

    if (active.data.current?.type !== "column") return;

    const activeContainer = findContainerOf(activeIdStr);
    const overContainer = renderGroupIds.includes(overIdStr) ? overIdStr : findContainerOf(overIdStr);
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;

    setColumns((prev) => {
      const newGroup = overContainer === UNGROUPED ? undefined : overContainer;
      return prev.map((c) => (c._key === activeIdStr ? { ...c, group: newGroup } : c));
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    const activeType = active.data.current?.type;

    if (activeType === "group") {
      if (activeIdStr === overIdStr || overIdStr === UNGROUPED) return;
      setGroups((prev) => {
        const oldIndex = prev.findIndex((g) => g.name === activeIdStr);
        const newIndex = prev.findIndex((g) => g.name === overIdStr);
        if (oldIndex < 0 || newIndex < 0) return prev;
        return arrayMove(prev, oldIndex, newIndex);
      });
      return;
    }

    if (activeType === "column") {
      const activeContainer = findContainerOf(activeIdStr);
      const overContainer = renderGroupIds.includes(overIdStr) ? overIdStr : findContainerOf(overIdStr);
      if (!activeContainer || !overContainer) return;

      setColumns((prev) => {
        const targetGroupKey = overContainer;
        const targetGroupValue = targetGroupKey === UNGROUPED ? undefined : targetGroupKey;
        const working = prev.map((c) => (c._key === activeIdStr ? { ...c, group: targetGroupValue } : c));
        const containerKeys = working.filter((c) => (c.group ?? UNGROUPED) === targetGroupKey).map((c) => c._key);
        const oldIndex = containerKeys.indexOf(activeIdStr);
        const newIndex = renderGroupIds.includes(overIdStr)
          ? containerKeys.length - 1
          : containerKeys.indexOf(overIdStr);
        if (oldIndex < 0 || newIndex < 0) return working;
        const reorderedKeys = arrayMove(containerKeys, oldIndex, newIndex);
        const inContainer = new Map(
          working.filter((c) => (c.group ?? UNGROUPED) === targetGroupKey).map((c) => [c._key, c]),
        );
        const reordered: DraftColumn[] = [];
        let cursor = 0;
        for (const c of working) {
          if ((c.group ?? UNGROUPED) === targetGroupKey) {
            reordered.push(inContainer.get(reorderedKeys[cursor++])!);
          } else {
            reordered.push(c);
          }
        }
        return reordered;
      });
    }
  };

  const validationError = useMemo(() => {
    if (columns.length === 0) return "At least one column is required.";
    if (columns.some((c) => !c.name.trim())) return "All columns must have a name.";
    const groupNames = groups.map((g) => g.name.trim().toLowerCase());
    if (groupNames.some((n) => !n)) return "All groups must have a name.";
    if (new Set(groupNames).size !== groupNames.length) return "Group names must be unique.";

    const seen = new Map<string, Set<string>>();
    for (const c of columns) {
      const g = c.group ?? UNGROUPED;
      if (!seen.has(g)) seen.set(g, new Set());
      const set = seen.get(g)!;
      const lower = c.name.trim().toLowerCase();
      if (set.has(lower)) {
        return `Duplicate column name "${c.name}" in ${g === UNGROUPED ? "Ungrouped" : g}.`;
      }
      set.add(lower);
    }
    return null;
  }, [columns, groups]);

  const handleOk = async (e: React.MouseEvent<HTMLButtonElement>) => {
    if (validationError || !boardId) return;

    const groupsPayload = groups.map((g, i) => ({
      name: g.name.trim(),
      position: i,
      ...(g.meta && Object.keys(g.meta).length ? { meta: g.meta } : {}),
    }));

    const payload: ColumnDto[] = [];

    let pos = 0;

    for (const gid of renderGroupIds) {
      const cols = grouped.get(gid) ?? [];

      const groupName = gid === UNGROUPED ? undefined : groups.find((g) => g.id === gid)?.name;

      for (const c of cols) {
        const { _key, ...rest } = c;

        payload.push({
          ...rest,
          group: groupName,
          position: pos++,
        });
      }
    }

    console.log({ columns, groupsPayload });
    editColumns.mutate(
      {
        _id: boardId!,
        columns: payload,
        groups: groupsPayload,
      },
      {
        onSuccess() {
          props.onOk?.(e);
          message.success("Updated columns of this board");
          props.refetch?.();
        },
      },
    );
  };

  const activeColumn = activeId ? columns.find((c) => c._key === activeId) : null;
  const activeGroup = activeId && groups.find((g) => g.name === activeId);

  const isLoading = getBoardQuery.isLoading;
  const isSaving = editColumns.isPending;

  return (
    <Modal
      {...props}
      title={title}
      okText={okText}
      width={760}
      onOk={handleOk}
      okButtonProps={{
        ...props.okButtonProps,
        loading: isSaving,
        disabled: !!validationError || isLoading,
      }}
      destroyOnClose
      styles={{ body: { maxHeight: "65vh", overflowY: "auto", paddingRight: 8 } }}
    >
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spin />
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <Text type="secondary" style={{ fontSize: 13 }}>
              Drag to reorder. Drop columns into any group or onto Ungrouped.
            </Text>
            <Space>
              <Button size="small" icon={<TbFolderPlus size={14} />} onClick={addGroup}>
                Add group
              </Button>
              <Button size="small" type="primary" icon={<TbPlus size={14} />} onClick={() => addColumn(undefined)}>
                Add column
              </Button>
            </Space>
          </div>

          {validationError && <Alert type="error" showIcon message={validationError} style={{ padding: "6px 12px" }} />}

          {columns.length === 0 && groups.length === 0 ? (
            <Empty description="No columns yet. Add your first column to get started." style={{ padding: "32px 0" }} />
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={groupOrder} strategy={verticalListSortingStrategy}>
                <div className="flex flex-col">
                  <SortableGroup
                    groupId={UNGROUPED}
                    groupName="Ungrouped"
                    isUngrouped
                    paletteIdx={0}
                    columns={grouped.get(UNGROUPED) ?? []}
                    resource={resource!}
                    orgCode={orgCode}
                    collapsed={!!collapsedGroups[UNGROUPED]}
                    onToggleCollapsed={() => toggleCollapsed(UNGROUPED)}
                    onRenameGroup={() => {}}
                    onColorChange={() => {}}
                    onDeleteGroup={() => {}}
                    onAddColumn={() => addColumn()}
                    onColumnChange={updateColumn}
                    onColumnMetaChange={updateColumnMeta}
                    onColumnDelete={deleteColumn}
                  />
                  {groups.map((g, idx) => (
                    <SortableGroup
                      key={g.id}
                      groupId={g.id}
                      groupName={g.name}
                      isUngrouped={false}
                      color={g.meta?.color}
                      paletteIdx={idx}
                      columns={grouped.get(g.id) ?? []}
                      resource={resource!}
                      orgCode={orgCode}
                      collapsed={!!collapsedGroups[g.id]}
                      onToggleCollapsed={() => toggleCollapsed(g.id)}
                      onRenameGroup={(newName) => renameGroup(g.id, newName)}
                      onColorChange={(color) => setGroupColor(g.id, color)}
                      onDeleteGroup={() => deleteGroup(g.id)}
                      onAddColumn={() => addColumn(g.id)}
                      onColumnChange={updateColumn}
                      onColumnMetaChange={updateColumnMeta}
                      onColumnDelete={deleteColumn}
                    />
                  ))}
                </div>
              </SortableContext>

              <DragOverlay>
                {activeColumn ? (
                  <div className="flex items-center gap-2 rounded-md border border-solid border-indigo-400 bg-white px-2 py-1.5 shadow-lg">
                    <TbGripVertical size={16} className="text-gray-400" />
                    {renderColumnIcon(activeColumn.meta?.icon, 16)}
                    <Text>{activeColumn.name}</Text>
                  </div>
                ) : activeGroup ? (
                  <div
                    className="rounded-lg px-3 py-2 shadow-lg border border-solid"
                    style={{
                      borderColor: getPaletteEntry(activeGroup.meta?.color, 0).border,
                      borderLeft: `3px solid ${getPaletteEntry(activeGroup.meta?.color, 0).solid}`,
                      background: getPaletteEntry(activeGroup.meta?.color, 0).soft,
                    }}
                  >
                    <Text strong>{activeGroup.name}</Text>
                  </div>
                ) : null}
              </DragOverlay>
            </DndContext>
          )}
        </div>
      )}
    </Modal>
  );
};
